// src/app/core/services/auth.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ApiResponse } from '@core/models/api-response.interface';
import { WorkspaceService } from '@core/services/workspace.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, finalize, Observable, tap } from 'rxjs';
import { ForgotPassword } from '../models/forgot-pass.interface';
import { Login, LoginResponse, User } from '../models/login.interface';
import { ResetPassword } from '../models/reset-pass.interface';

const BASE_URL = environment.apiBaseUrl;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokenKey = 'token';
  private userKey = 'user';
  isLoggedIn = signal<boolean>(false);
  isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  //primeng design token
  customSelect = {
    colorScheme: {
      light: {
        root: {
          borderColor: 'transparent',
          hoverBorderColor: 'transparent',
          focusBorderColor: 'transparent',
          shadow: 'none',
          lg: {
            fontSize: '12px',
            paddingX: '0',
            paddingY: '0',
          },
        },
      },
    },
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private workspaceService: WorkspaceService,
  ) {}

  login(data: Login): Observable<LoginResponse> {
    this.isLoadingSubject.next(true);
    return this.http.post<LoginResponse>(`${BASE_URL}auth/login`, data).pipe(
      tap((res) => {
        this.saveToken(res.data.accessToken);
        localStorage.setItem(this.userKey, JSON.stringify(res.data.user));
      }),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  saveToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
    this.isLoggedIn.set(true);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  getUser(): User | null {
    const userData = localStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  logOut() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
    this.workspaceService.clearWorkspace();
    this.isLoggedIn.set(false);
    this.router.navigate(['/auth/login']);
  }

  // To send email on forgot password
  forgotPassword(data: ForgotPassword) {
    this.isLoadingSubject.next(true);
    return this.http.post<ApiResponse<any>>(`${BASE_URL}auth/forgot-password`, data).pipe(
      tap(),
      finalize(() => this.isLoadingSubject.next(false)),
    );
  }

  // To reset password on forgot password
  // adding temporory token received through email (link url) on forgot password
  resetPassword(data: ResetPassword, token: string) {
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(`${BASE_URL}auth/reset-password`, data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        tap(),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  // api call on checking token in guard(reset-guard.guard.ts)
  resetTokenVerify(token: string): Observable<any> {
    return this.http
      .get<any>(`${BASE_URL}auth/reset-password/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        tap((res) => {
          console.log('Verify API response:', res);
        }),
      );
  }

  // adding temporory token received through email (link url) on create user through link
  setPassword(data: ResetPassword, token: string) {
    this.isLoadingSubject.next(true);
    return this.http
      .post<any>(`${BASE_URL}auth/set-password`, data, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        tap(),
        finalize(() => this.isLoadingSubject.next(false)),
      );
  }

  // api call on checking token in guard(set-guard.guard.ts)
  setTokenVerify(token: string): Observable<any> {
    return this.http
      .get<any>(`${BASE_URL}auth/set-password/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        tap((res) => {
          console.log('Verify API response:', res);
        }),
      );
  }

  getDesignTokens() {
    return this.customSelect;
  }
}
